import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useAppStateContext } from 'src/AppState/AppState';
import { getLanguageName } from 'src/components/TranslationsContent/languages';
import { AddTableRow } from 'src/components/TranslationsContent/SelectedLanguage/AddTableRow/AddTableRow';
import { EditableTableRow } from 'src/components/TranslationsContent/SelectedLanguage/EditableTableRow/EditableTableRow';
import {
    ActionButton,
    BackButton,
    ButtonsWrapper,
    HeaderButton,
    HeaderLinkButton,
    HeaderNav,
    Hr,
    TableBodyCell,
    TableBodyRow,
    TableHeaderCell,
    TableHeaderRow,
    TableWrapper,
} from 'src/components/TranslationsContent/SelectedLanguage/SelectedLanguage.style';
import { TranslationFilter } from 'src/components/TranslationsContent/SelectedLanguage/TranslationFilter/TranslationFilter';
import {
    SelectLanguageDetails,
    TranslationsContentState,
} from 'src/components/TranslationsContent/TranslationsContent.state';
import { SelectedLanguageState } from './SelectedLanguage.state';

type Props = {
    translationsContentState: TranslationsContentState;
    selectLanguageDetails: SelectLanguageDetails;
};

export const SelectedLanguage = observer(({ translationsContentState, selectLanguageDetails }: Props) => {
    const [state] = useState(() => new SelectedLanguageState());
    const appState = useAppStateContext();

    const uniqueKeys: string[] = Object.keys(selectLanguageDetails.properties).filter(x => x !== state.editedKey);

    const languageName = getLanguageName(selectLanguageDetails.language);
    const { selectedPageType } = appState.translationPageState;
    const allTranslationList = Object.entries(selectLanguageDetails.properties);

    const arrStatic: [string, string][] = [];
    const arrFeed: [string, string][] = [];

    for (const [key, value] of allTranslationList) {
        if (key.startsWith('_market') || key.startsWith('_selection')) {
            arrFeed.push([key, value]);
        } else {
            arrStatic.push([key, value]);
        }
    }

    return (
        <>
            <HeaderNav>
                <BackButton onClick={(): void => translationsContentState.handleBack()}>‹ {languageName}</BackButton>

                <ButtonsWrapper>
                    <HeaderLinkButton
                        href={`/api/translations/${translationsContentState.groupName}/csv/${selectLanguageDetails.language}`}
                        download
                    >
                        Export CSV File
                    </HeaderLinkButton>

                    <HeaderButton onClick={state.onAddClick}>+ Add New Key</HeaderButton>
                </ButtonsWrapper>
            </HeaderNav>

            <TranslationFilter />
            <Hr />

            <TableWrapper>
                <thead>
                    <TableHeaderRow>
                        <TableHeaderCell>Key</TableHeaderCell>
                        <TableHeaderCell>Language Translation</TableHeaderCell>
                        <TableHeaderCell />
                    </TableHeaderRow>
                </thead>

                <tbody>
                    {state.adding && (
                        <AddTableRow
                            key='addTranslationProperty'
                            language={selectLanguageDetails.language}
                            groupName={translationsContentState.groupName}
                            onCancel={state.onCancelClick}
                            onUpdate={state.onUpdate}
                            uniqueKeys={selectLanguageDetails.getUniqueKeys(state.editedKey)}
                            translationsContentState={translationsContentState}
                        />
                    )}

                    {(selectedPageType === 'static-translation' ? arrStatic : arrFeed)
                        .sort(state.sortElement)
                        .map(([key, value]) =>
                            state.editedKey === key ? (
                                <EditableTableRow
                                    key={key}
                                    language={selectLanguageDetails.language}
                                    uniqueKeys={uniqueKeys}
                                    translationKey={key}
                                    translationValue={value}
                                    onCancel={state.onCancelClick}
                                    groupName={translationsContentState.groupName}
                                    onUpdate={async (): Promise<void> => {
                                        await translationsContentState.refreshLanguages();
                                        state.onUpdate();
                                    }}
                                />
                            ) : (
                                <TableBodyRow key={key}>
                                    <TableBodyCell>{key}</TableBodyCell>
                                    <TableBodyCell>{value}</TableBodyCell>
                                    {state.adding || state.editedKey !== null ? (
                                        <TableBodyCell />
                                    ) : (
                                        <TableBodyCell>
                                            <ActionButton onClick={state.onEditClick} data-key={key}>
                                                Edit
                                            </ActionButton>
                                        </TableBodyCell>
                                    )}
                                </TableBodyRow>
                            )
                        )}
                </tbody>
            </TableWrapper>
        </>
    );
});
